// frontend/src/components/auditor/AuditorCompliance.js
// Compliance items, annual certificates, and conflict checks for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'compliance', label: 'Compliance Items' },
  { id: 'certificates', label: 'Annual Certificates' },
  { id: 'conflicts', label: 'Conflict Checks' },
];

const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

// [WF-B12] The removed PriorityBadge/`priority` column had no backing
// field anywhere — ComplianceItem has no priority concept at all (see the
// model's field list) — so it was deleted rather than pointed at a
// still-wrong name; there's nothing real to fall back to.

export default function AuditorCompliance({ authAxios }) {
  const [subTab, setSubTab] = useState('compliance');
  const [complianceItems, setComplianceItems] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [ci, cert, conf] = await Promise.all([
      fetchAllPages(authAxios, 'compliance-items/'),
      fetchAllPages(authAxios, 'annual-certificates/'),
      fetchAllPages(authAxios, 'conflict-checks/'),
    ]);
    setComplianceItems(ci);
    setCertificates(cert);
    setConflicts(conf);
    if (ci.length === 0 && cert.length === 0 && conf.length === 0) {
      setFetchError('No compliance data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Stats
  // [Auditor figures reconciliation] ComplianceItem.status only has four
  // real choices — COMPLIANT, NON_COMPLIANT, IN_PROGRESS, NOT_APPLICABLE
  // (legal_ops/models.py) — 'Completed'/'COMPLETED'/'Overdue'/'OVERDUE'
  // were never valid values, so "Completed" always read 0 and "Overdue"
  // silently fell back to "has a past due_date" with no exclusion for
  // already-resolved items (the c.status !== 'Completed' guard never
  // fired either, since that string never matches).
  const completedItems = complianceItems.filter(c => c.status === 'COMPLIANT').length;
  const overdueItems = complianceItems.filter(c =>
    c.due_date && new Date(c.due_date) < new Date() && c.status !== 'COMPLIANT' && c.status !== 'NOT_APPLICABLE'
  ).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Compliance</h2>
        <p className="text-sm text-gray-500 mt-1">Compliance items, annual certificates, and conflict-of-interest checks.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: complianceItems.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Completed', value: completedItems, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Overdue', value: overdueItems, color: 'text-red-700 bg-red-50 border-red-200' },
          { label: 'Conflict Checks', value: conflicts.length, color: 'text-purple-700 bg-purple-50 border-purple-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'compliance' && (
        <AuditorTable
          title={`Compliance Items (${complianceItems.length})`}
          data={complianceItems}
          columns={[
            // [WF-B12] ComplianceItem's real fields are `requirement` (not
            // title/name) and `regulation` (not category) — both were
            // blank for every row.
            { key: 'requirement', label: 'Requirement', render: (v, row) => row.requirement || '—' },
            { key: 'regulation_display', label: 'Regulation', render: (v, row) => row.regulation_display || row.regulation || '—' },
            { key: 'status', label: 'Status', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'COMPLIANT' ? 'bg-green-100 text-green-700' :
                v === 'NON_COMPLIANT' ? 'bg-red-100 text-red-700' :
                v === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                v === 'NOT_APPLICABLE' ? 'bg-gray-100 text-gray-500' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'due_date', label: 'Due Date', render: (v) => fmtDate(v) },
            // [WF-B12] Real field is `assigned_to_username` — `assigned_to_name`/
            // `assigned_to_details` never existed.
            { key: 'assigned_to_username', label: 'Assigned To', render: (v, row) => row.assigned_to_username || '—' },
            { key: 'completed_date', label: 'Completed', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'compliance_items.csv' }}
          emptyMessage="No compliance items found."
        />
      )}

      {subTab === 'certificates' && (
        <AuditorTable
          title={`Annual Certificates (${certificates.length})`}
          data={certificates}
          columns={[
            { key: 'certificate_type_display', label: 'Type', render: (v, row) => row.certificate_type_display || row.certificate_type || '—' },
            // [WF-B12] Real field is `practitioner_name` — `staff_name`/
            // `staff_details` never existed.
            { key: 'practitioner_name', label: 'Practitioner', render: (v, row) => row.practitioner_name || '—' },
            { key: 'year', label: 'Year' },
            // [WF-B12] AnnualCertificate.status is a submission workflow
            // (PENDING/SUBMITTED/APPROVED/REJECTED), not a validity state
            // (Valid/Expired) — none of the checked values ever matched,
            // so this badge always fell to the gray default.
            { key: 'status', label: 'Status', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'APPROVED' ? 'bg-green-100 text-green-700' :
                v === 'REJECTED' ? 'bg-red-100 text-red-700' :
                v === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                v === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            // [WF-B12] `issue_date`/`expiry_date` don't exist on this model
            // at all — the only date field is `submission_date`. No
            // substitute exists for "Expires", so that column is removed
            // rather than pointed at invented data.
            { key: 'submission_date', label: 'Submitted', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'annual_certificates.csv' }}
          emptyMessage="No annual certificates found."
        />
      )}

      {subTab === 'conflicts' && (
        <AuditorTable
          title={`Conflict Checks (${conflicts.length})`}
          data={conflicts}
          columns={[
            // [WF-B12] ConflictCheckSerializer's real fields: `case_title`
            // (not case_reference/case_details), `client_full_name` (not
            // client_name/client_details), `status` (not `result` — that
            // key never existed at all, so the badge below was comparing
            // against `undefined` regardless of how close its string
            // values looked to the real STATUS_CHOICES), `checked_by_username`
            // (not checked_by_name/checked_by_details), `check_date` (not
            // date_checked), `resolution_notes` (not notes).
            { key: 'case_title', label: 'Case', render: (v, row) => row.case_title || '—' },
            { key: 'client_full_name', label: 'Client', render: (v, row) => row.client_full_name || '—' },
            { key: 'status', label: 'Result', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'CLEAR' ? 'bg-green-100 text-green-700' :
                v === 'POTENTIAL' ? 'bg-amber-100 text-amber-700' :
                v === 'WAIVED' ? 'bg-blue-100 text-blue-700' :
                v === 'DECLINED' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'checked_by_username', label: 'Checked By', render: (v, row) => row.checked_by_username || '—' },
            { key: 'check_date', label: 'Date Checked', render: (v) => fmtDate(v) },
            { key: 'resolution_notes', label: 'Notes' },
          ]}
          exportCsv={{ filename: 'conflict_checks.csv' }}
          emptyMessage="No conflict checks found."
        />
      )}
    </div>
  );
}
